import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "nativewind/jsx-runtime";
/* eslint-disable no-restricted-imports --
   이미지 클릭은 offsetX/offsetY 가 필요해 raw Pressable 로 받고 useLogger 로 직접 로깅한다.
   PointMarker/AddRegionButtonOverlay 는 편집 모드 전용 도구라 계측 대상이 아니다.
   (터치 컴포넌트는 Scc* 사용 원칙: .eslintrc.js no-restricted-imports) */
import React, { useState, useCallback, useRef } from 'react';
import { View, Image, Text, Pressable, TouchableOpacity } from 'react-native';
import styled from 'styled-components/native';
import Svg, { Polygon, Line } from 'react-native-svg';
import { color } from '@/constant/color';
import { useLogger } from '@/logging/useLogger';
import { useEditMode } from '../context/EditModeContext';
import { useResponsive } from '../context/ResponsiveContext';
import ImageUploader from './ImageUploader';
import RegionDetailModal from './RegionDetailModal';
export default function InteractiveImage({ interactiveImage, onImageChange, routeIndex = 0, sectionType = 'route', hintTextBackgroundColor, }) {
    var _a, _b;
    const editContext = useEditMode();
    const isEditMode = (_a = editContext === null || editContext === void 0 ? void 0 : editContext.isEditMode) !== null && _a !== void 0 ? _a : false;
    const editingRegion = (_b = editContext === null || editContext === void 0 ? void 0 : editContext.editingRegion) !== null && _b !== void 0 ? _b : null;
    const { isDesktop } = useResponsive();
    const logger = useLogger();
    const loggerRef = useRef(logger);
    loggerRef.current = logger;
    // 데스크탑이 아니면 모바일 이미지 우선 사용
    const displayImageUrl = isDesktop
        ? interactiveImage.url
        : (interactiveImage.mobileUrl || interactiveImage.url);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [containerWidth, setContainerWidth] = useState(0);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const handleContainerLayout = useCallback((event) => {
        const { width } = event.nativeEvent.layout;
        setContainerWidth(width);
    }, []);
    const handleImageLoad = useCallback(() => {
        Image.getSize(displayImageUrl, (width, height) => {
            setImageSize({ width, height });
        }, (error) => {
            console.error('Failed to get image size:', error);
        });
    }, [displayImageUrl]);
    // Calculate display height maintaining aspect ratio
    const displayHeight = containerWidth && imageSize.width
        ? (containerWidth * imageSize.height) / imageSize.width
        : 0;
    // Convert relative polygon points (0-1) to absolute coordinates
    const getPolygonPoints = (region) => {
        return region.polygon
            .map((point) => {
            const x = point.x * containerWidth;
            const y = point.y * displayHeight;
            return `${x},${y}`;
        })
            .join(' ');
    };
    // Convert editing region points to polygon string
    const getEditingPolygonPoints = () => {
        if (!editingRegion)
            return '';
        return editingRegion.points
            .map((point) => {
            const x = point.x * containerWidth;
            const y = point.y * displayHeight;
            return `${x},${y}`;
        })
            .join(' ');
    };
    const clickableRegions = interactiveImage.clickableRegions || [];
    // 현재 이 InteractiveImage가 편집 중인지 확인
    const isEditingThisImage = (editingRegion === null || editingRegion === void 0 ? void 0 : editingRegion.sectionType) === sectionType &&
        (editingRegion === null || editingRegion === void 0 ? void 0 : editingRegion.routeIndex) === routeIndex;
    // 이미지 클릭 시 점 추가 (편집 중일 때) 또는 non-region 클릭 로깅 (view 모드)
    const handleImageClick = useCallback((event) => {
        if (isEditMode && isEditingThisImage && (editContext === null || editContext === void 0 ? void 0 : editContext.addPointToRegion)) {
            // 편집 모드: 점 추가
            const { offsetX, offsetY } = event.nativeEvent;
            // 상대 좌표로 변환 (0-1)
            const relativeX = offsetX / containerWidth;
            const relativeY = offsetY / displayHeight;
            // 경계 체크
            if (relativeX < 0 || relativeX > 1 || relativeY < 0 || relativeY > 1) {
                return;
            }
            editContext.addPointToRegion({ x: relativeX, y: relativeY });
        }
        else if (!isEditMode) {
            // View 모드: non-region 영역 클릭 로깅
            const { offsetX, offsetY } = event.nativeEvent;
            const relativeX = offsetX / containerWidth;
            const relativeY = offsetY / displayHeight;
            loggerRef.current.logElementClick('bbucle-road-interactive-image-non-region-click', {
                imageUrl: interactiveImage.url,
                sectionType,
                routeIndex,
                clickX: relativeX,
                clickY: relativeY,
            });
        }
    }, [isEditMode, isEditingThisImage, editContext, containerWidth, displayHeight, interactiveImage.url, sectionType, routeIndex]);
    // Region 클릭 핸들러
    const handleRegionClick = useCallback((region, regionIndex) => {
        if (isEditMode) {
            // Edit mode: 해당 region 편집 시작
            if (sectionType === 'seatView' && (editContext === null || editContext === void 0 ? void 0 : editContext.startEditingSeatViewRegion)) {
                editContext.startEditingSeatViewRegion(regionIndex, region.polygon, region.modalImageUrls || []);
            }
            else if (sectionType === 'route' && (editContext === null || editContext === void 0 ? void 0 : editContext.startEditingRegion)) {
                editContext.startEditingRegion(routeIndex, regionIndex, region.polygon, region.modalImageUrls || []);
            }
        }
        else {
            // View mode: 로깅 후 모달 열기
            loggerRef.current.logElementClick('bbucle-road-interactive-image-region', {
                imageUrl: interactiveImage.url,
                regionId: region.id,
                sectionType,
                routeIndex,
            });
            setSelectedRegion(region);
        }
    }, [isEditMode, editContext, routeIndex, sectionType, interactiveImage.url]);
    const handleCloseModal = useCallback(() => {
        setSelectedRegion(null);
    }, []);
    const handleImageUpload = useCallback((url) => {
        if (onImageChange) {
            onImageChange(url);
        }
    }, [onImageChange]);
    // 편집 중인 점 제거
    const handleRemovePoint = useCallback((pointIndex) => {
        if (editContext === null || editContext === void 0 ? void 0 : editContext.removePointFromRegion) {
            editContext.removePointFromRegion(pointIndex);
        }
    }, [editContext]);
    // 상대 좌표를 픽셀 좌표로 변환
    const toPixelX = (x) => x * containerWidth;
    const toPixelY = (y) => y * displayHeight;
    return (_jsxs(Container, { onLayout: handleContainerLayout, children: [isEditMode && onImageChange && (_jsxs(_Fragment, { children: [_jsx(AddRegionButtonOverlay, { onPress: () => {
                            if (sectionType === 'seatView') {
                                editContext === null || editContext === void 0 ? void 0 : editContext.startAddingSeatViewRegion();
                            }
                            else {
                                editContext === null || editContext === void 0 ? void 0 : editContext.startAddingRegion(routeIndex);
                            }
                        }, children: _jsx(AddRegionButtonText, { children: "+ Region" }) }), _jsx(EditImageOverlay, { children: _jsx(ImageUploader, { currentImageUrl: interactiveImage.url, onUploadComplete: handleImageUpload, compact: true }) })] })), _jsx(Pressable, { onPress: handleImageClick, children: _jsx(StyledImage, { source: { uri: displayImageUrl }, style: { height: displayHeight || 'auto' }, resizeMode: "contain", onLoad: handleImageLoad }) }), displayHeight > 0 && (_jsxs(SvgOverlay, { width: containerWidth, height: displayHeight, pointerEvents: isEditingThisImage ? 'none' : 'box-none', children: [clickableRegions.map((region, index) => {
                        // 현재 편집 중인 region은 다르게 표시
                        const isEditing = isEditingThisImage && (editingRegion === null || editingRegion === void 0 ? void 0 : editingRegion.regionIndex) === index;
                        return (_jsx(Polygon, { points: getPolygonPoints(region), fill: isEditMode
                                ? isEditing
                                    ? 'rgba(255, 165, 0, 0.3)' // 편집 모드 & 편집 중: 주황색
                                    : 'rgba(0, 122, 255, 0.2)' // 편집 모드 & 일반: 파란색
                                : 'rgba(0, 0, 0, 0)' // view 모드: 투명
                            , stroke: isEditMode
                                ? isEditing
                                    ? 'rgba(255, 165, 0, 0.8)'
                                    : 'rgba(0, 122, 255, 0.5)'
                                : 'rgba(0, 0, 0, 0)', strokeWidth: 2, onPress: !isEditingThisImage
                                ? () => handleRegionClick(region, index)
                                : undefined }, region.id));
                    }), isEditingThisImage &&
                        (editingRegion === null || editingRegion === void 0 ? void 0 : editingRegion.regionIndex) === null &&
                        editingRegion.points.length > 0 && (_jsxs(_Fragment, { children: [editingRegion.points.length >= 3 && (_jsx(Polygon, { points: getEditingPolygonPoints(), fill: "rgba(0, 200, 100, 0.2)", stroke: "rgba(0, 200, 100, 0.8)", strokeWidth: 2 })), editingRegion.points.length >= 2 &&
                                editingRegion.points.map((point, index) => {
                                    if (index === 0)
                                        return null;
                                    const prevPoint = editingRegion.points[index - 1];
                                    return (_jsx(Line, { x1: toPixelX(prevPoint.x), y1: toPixelY(prevPoint.y), x2: toPixelX(point.x), y2: toPixelY(point.y), stroke: "rgba(0, 200, 100, 0.8)", strokeWidth: 2 }, `line-${index}`));
                                }), editingRegion.points.length >= 3 && (_jsx(Line, { x1: toPixelX(editingRegion.points[editingRegion.points.length - 1].x), y1: toPixelY(editingRegion.points[editingRegion.points.length - 1].y), x2: toPixelX(editingRegion.points[0].x), y2: toPixelY(editingRegion.points[0].y), stroke: "rgba(0, 200, 100, 0.8)", strokeWidth: 2, strokeDasharray: "5,5" }))] }))] })), isEditingThisImage &&
                editingRegion &&
                displayHeight > 0 &&
                editingRegion.points.map((point, index) => (_jsx(PointMarker, { style: {
                        left: toPixelX(point.x) - 10,
                        top: toPixelY(point.y) - 10,
                    }, onPress: () => handleRemovePoint(index), children: _jsx(PointMarkerText, { children: index + 1 }) }, `point-${index}`))), interactiveImage.clickableRegions.length > 0 &&
                _jsx(HintContainer, { isDesktop: isDesktop, hintTextBackgroundColor: hintTextBackgroundColor, children: _jsx(HintText, { isDesktop: isDesktop, children: "\uC0AC\uC9C4\uC744 \uD074\uB9AD\uD558\uBA74, \uD06C\uAC8C \uBCFC \uC218 \uC788\uC2B5\uB2C8\uB2E4 \uD83D\uDD0D" }) }), _jsx(RegionDetailModal, { visible: !!selectedRegion, allRegions: (interactiveImage === null || interactiveImage === void 0 ? void 0 : interactiveImage.clickableRegions) || [], initialRegionId: selectedRegion === null || selectedRegion === void 0 ? void 0 : selectedRegion.id, onClose: handleCloseModal })] }));
}
const Container = styled(View) `
  position: relative;
  width: 100%;
`;
const StyledImage = styled(Image) `
  width: 100%;
`;
const SvgOverlay = styled(Svg) `
  position: absolute;
  top: 0;
  left: 0;
`;
const EditImageOverlay = styled(View) `
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
`;
const PointMarker = styled(Pressable) `
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 10px;
  background-color: ${color.success30};
  align-items: center;
  justify-content: center;
  z-index: 20;
  border: 2px solid ${color.white};
`;
const PointMarkerText = styled(Text) `
  color: ${color.white};
  font-size: 10px;
  font-weight: 700;
`;
const AddRegionButtonOverlay = styled(TouchableOpacity) `
  position: absolute;
  top: 8px;
  right: 50px;
  z-index: 10;
  padding: 4px 8px;
  background-color: ${color.iosBlue};
  border-radius: 4px;
`;
const AddRegionButtonText = styled(Text) `
  font-size: 11px;
  font-weight: 600;
  color: ${color.white};
`;
const HintContainer = styled(View) `
  background-color: ${({ hintTextBackgroundColor }) => hintTextBackgroundColor !== null && hintTextBackgroundColor !== void 0 ? hintTextBackgroundColor : color.gray10};
  padding: ${({ isDesktop }) => (isDesktop ? '8px' : '6px')} 0;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  margin: ${({ isDesktop }) => (isDesktop ? '0 0 0' : '0 0 0')};
`;
const HintText = styled(Text) `
  font-size: ${({ isDesktop }) => (isDesktop ? '15px' : '13px')};
  line-height: ${({ isDesktop }) => (isDesktop ? '22px' : '18px')};
  font-weight: 500;
  color: #16181c;
`;
